import { useState, useEffect, ReactNode, useRef, forwardRef } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { IconButton, Tooltip } from "@mui/material";

interface CustomTextFieldProps {
  type?: string;
  label: string;
  placeholder?: string;
  is_required?: boolean;
  manualError?: boolean;
  is_multiline?: boolean;
  errorText?: string;
  value: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: any;
  [key: string]: any;
  endAdornment?: ReactNode;
  inputMode?: "integer" | "decimal" | "text" | "singledecimal";
  is_disabled?: boolean;
  sx?: any;
  addCopyButton?: boolean;
  addEraseButton?: boolean;
  eraseText?: string;
  addDisableButton?: boolean;
  minLength?: number;
  maxLength?: number;
}

const CustomTextField = forwardRef<HTMLInputElement, CustomTextFieldProps>(({
                                                                              type = "text",
                                                                              label,
                                                                              placeholder = "",
                                                                              is_required = false,
                                                                              manualError = false,
                                                                              is_multiline = false,
                                                                              errorText = "",
                                                                              value,
                                                                              onChange = () => {},
                                                                              onBlur = () => {},
                                                                              endAdornment,
                                                                              inputMode = "text",
                                                                              is_disabled = false,
                                                                              addCopyButton = false,
                                                                              addEraseButton = false,
                                                                              autocomplete = "off",
                                                                              eraseText = "",
                                                                              addDisableButton = false,
                                                                              minLength,
                                                                              maxLength,
                                                                              sx = {},
                                                                              ...props
                                                                            }, ref) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState(false);
  const [internalErrorText, setInternalErrorText] = useState(errorText);
  const [isDisabled, setIsDisabled] = useState(is_disabled);

  const inputRef = useRef<HTMLInputElement | null>(null);

  // Sync the forwarded ref with internal ref
  useEffect(() => {
    if (typeof ref === 'function') {          // i.e. callback ref (ref is a function)
      ref(inputRef.current);
    } else if (ref) {                         // i.e. ref created with useRef()
      (ref as React.MutableRefObject<HTMLInputElement | null>).current = inputRef.current;
    }
  }, [ref]);

  useEffect(() => {
    setInternalErrorText(errorText);
  }, [errorText]);

  useEffect(() => {
    if (isDisabled !== is_disabled) {
      setIsDisabled(is_disabled);
    }
  }, [is_disabled]);

  useEffect(() => {
    setInternalError(manualError);
  }, [manualError]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === "") {
      setTouched(true);
    }

    if (inputMode === "decimal" || inputMode === "singledecimal") {
      if (event.target.value.endsWith(".")) {
        setInternalError(true);
        setInternalErrorText("Die Zahl darf nicht mit einem \".\" enden");
        return;
      }
    }

    if (minLength && event.target.value.length < minLength) {
      setInternalError(true);
      setInternalErrorText(`Mindestens ${minLength} Zeichen erforderlich`);
      return;
    }

    if (maxLength && event.target.value.length > maxLength) {
      setInternalError(true);
      setInternalErrorText(`Maximal ${maxLength} Zeichen erlaubt`);
      return;
    }

    if (!manualError) {
      setInternalError(false);
      setInternalErrorText(errorText);
    }

    onBlur(event);
  };

  const handleFocus = () => {
    setTouched(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    let inputValue = event.target.value;

    if (inputMode === "integer") {
      inputValue = inputValue.replace(/[^0-9]/g, '');
    } else if (inputMode === "decimal" || inputMode === "singledecimal") {
      inputValue = inputValue.replace(/[^0-9.,]/g, '').replace(',', '.');
      const max_decimal_length = inputMode === "decimal" ? 2 : 1;
      const [intPart, decPart] = inputValue.split('.');
      if (decPart !== undefined) {
        inputValue = `${intPart}.${decPart.slice(0, max_decimal_length)}`;
      }
      if (inputValue === ".") inputValue = "0.";
    }

    if (maxLength && inputValue.length > maxLength) {
      inputValue = inputValue.slice(0, maxLength);
    }

    onChange({ ...event, target: { ...event.target, value: inputValue } });
  };

  const EmptyError = is_required && touched && value === "";
  const EmptyErrorMessage = "Dieses Feld muss ausgefüllt sein";

  const finalLabel = label + (is_required ? "*" : "");

  return (
      <TextField
          className={internalError || EmptyError ? "custom-text-field-error" : "custom-text-field"}
          type={type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          label={finalLabel}
          autoComplete={autocomplete}
          onBlur={handleBlur}
          onFocus={handleFocus}
          helperText={EmptyError ? EmptyErrorMessage : internalError ? internalErrorText : ""}
          multiline={is_multiline}
          disabled={isDisabled}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
                <>
                  {addCopyButton && (
                      <Tooltip title="Copy">
                        <IconButton
                            onClick={() => {
                              navigator.clipboard.writeText(value).catch(e => {
                                alert("Text konnte nicht kopiert werden");
                                console.log(e);
                              });
                              inputRef.current?.focus();
                            }}
                            size="small"
                            sx={{ marginRight: "-4px" }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                  )}
                  {addEraseButton && (
                      <Tooltip title="Clear">
                        <IconButton
                            onClick={() => {
                              onChange({ target: { value: eraseText } } as React.ChangeEvent<HTMLInputElement>);
                              requestAnimationFrame(() => inputRef.current?.focus());
                            }}
                            size="small"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                  )}
                  {addDisableButton && (
                      <Tooltip title={isDisabled ? "Entsperren" : "Sperren"}>
                        <IconButton
                            onClick={() => setIsDisabled(!isDisabled)}
                            size="small"
                        >
                          {isDisabled ? (
                              <LockOutlinedIcon fontSize="small" />
                          ) : (
                              <LockOpenOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                  )}
                  {endAdornment && (
                      <InputAdornment position="end">{endAdornment}</InputAdornment>
                  )}
                </>
            )
          }}
          inputProps={{
            minLength: minLength,
            maxLength: maxLength,
            inputMode: inputMode === "integer" || inputMode === "decimal" || inputMode === "singledecimal" ? "numeric" : "text",
            pattern: inputMode === "integer" ? "[0-9]*" : undefined
          }}
          sx={{
            width: '50%',
            maxWidth: '100%',
            '& .MuiInputLabel-root': {
              color: 'var(--font-primary) !important',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: internalError || EmptyError ? 'red' : 'var(--naturGreen)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--naturGreen)',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--naturGreen)',
            },
            ...sx,
          }}
          {...props}
      />
  );
});

export default CustomTextField;
